import React from 'react';
import {
  IconHolder,
  StyledCell,
  StyledProductCellContainer,
  StyledRow,
} from '../../../components/StructureTable/index.styles';
import { Checkbox, CircularProgress, IconButton, Switch } from '@mui/material';
import CheckCircleSharpIcon from '@mui/icons-material/CheckCircleSharp';
import EditSharpIcon from '@mui/icons-material/EditSharp';
import {
  CellType,
  OrganizationTreeNode,
} from '../../../components/StructureTable/index.types';
import { useNavigate } from 'react-router-dom';
import { BranchTagWithCountry } from 'src/utils/branches';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { client } from 'src/utils/api-client';
import { toast } from 'react-toastify';
import { OrganizationTreeNodeSchema } from 'src/Superadmin/components/StructureTable/index.types';
import { CountryRollup } from 'src/Superadmin/components/CountryGroupHeader';

// specs/001-raben-country-masteradmin/mass-specs/specs/
// 20260826-fe-country-group-folding.md. Not in that spec's own Affected
// Files list (only Users/UsersTable/{Row,SupplierRow}.tsx are named for
// "roll-up cell") -- but Goal 2/BR-2's "a collapsed group shows a roll-up,
// never hides access state" applies to Structure exactly as much as to
// Users, and there is nowhere else to render it: StructureTable.tsx builds
// the header from `groups`, but each ORGANIZATION's own on/off state per
// branch only exists here, per row. Rendering it required widening this
// file's prop contract from a flat `branches` list to the same
// `groups`-shaped prop Users/UsersTable/Row.tsx already takes for the
// identical reason. Treated as the same omission class as that file, not a
// new one.
interface StructureBranchGroup {
  key: string;
  branches: BranchTagWithCountry[];
  isOpen: boolean;
}

interface Props {
  organization: OrganizationTreeNode;
  depth?: number;
  // Visible branch columns, already narrowed to the active country filter,
  // grouped by country, and resolved to open/collapsed by StructureTable --
  // kept as a prop (rather than this component calling useBranches()/
  // useBranchesWithCountry()/the collapse hook itself) so the row's cell
  // set always matches the header's, filtered or collapsed or not.
  groups: StructureBranchGroup[];
  // Single-column width, shared with StructureTable's own header rows so an
  // open branch switch and a collapsed roll-up cell are always exactly as
  // wide as one "visual column" -- see StructureTable.tsx's comment on
  // `totalVisualColumns` for why this must be computed once and threaded
  // through, not re-derived here.
  columnWidth: string;
}

export default function Row(props: Props) {
  const { organization, groups, columnWidth } = props;
  const { name, depth, can_edit } = organization;
  const cellType: CellType = 'unit';

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const updateBranchInTree = (
    nodes: OrganizationTreeNodeSchema[],
    targetId: number,
    branchName: string,
    value: boolean
  ): OrganizationTreeNodeSchema[] => {
    return nodes.map((node) => {
      const updatedChildren = node.children?.length
        ? updateBranchInTree(node.children, targetId, branchName, value)
        : node.children;

      if (node.id !== targetId) {
        // preserve reference when nothing changes in this subtree
        if (updatedChildren === node.children) return node;
        return { ...node, children: updatedChildren };
      }

      const nextBranches = value
        ? Array.from(new Set([...(node.branches || []), branchName]))
        : (node.branches || []).filter((b) => b !== branchName);

      return { ...node, branches: nextBranches, children: updatedChildren };
    });
  };

  const setOrganizationBranch = useMutation({
    mutationFn: (data: { branch: string; value: boolean }) =>
      client.put(
        `/web/settings_v2/organizations/${organization.id}/branches`,
        data
      ),
    onMutate: async (variables) => {
      // Optimistic update: update the cached tree immediately so the switch flips instantly.
      await queryClient.cancelQueries('organization-structure');
      const previous = queryClient.getQueryData<OrganizationTreeNodeSchema[]>(
        'organization-structure'
      );

      if (previous) {
        queryClient.setQueryData<OrganizationTreeNodeSchema[]>(
          'organization-structure',
          updateBranchInTree(
            previous,
            organization.id,
            variables.branch,
            variables.value
          )
        );
      }

      return { previous };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData('organization-structure', context.previous);
      }
      toast.error('Failed to update organization branches');
    },
    onSettled: () => {
      // Keep server as source of truth, but UI is already updated.
      queryClient.invalidateQueries('organization-structure');
    },
  });

  const handleEditClick = () => {
    navigate(`/superadmin/org-structure/unit/${organization.id}`);
  };

  return (
    <StyledRow>
      <StyledCell
        type={cellType}
        w="40%"
        depth={depth}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {name}

        {can_edit && (
          <div style={{ minWidth: '40px' }}>
            <IconButton onClick={handleEditClick}>
              <EditSharpIcon sx={{ color: '#6E6E6E' }} />
            </IconButton>
          </div>
        )}
      </StyledCell>
      {groups.map((group) => {
        if (!group.isOpen) {
          // Goal 2/BR-2/EC-2/EC-7: collapsed -- a roll-up fraction over
          // this ORGANIZATION's own on/off state for the group's branches,
          // never a blank cell. BR-3: total is `group.branches.length`,
          // i.e. exactly the caller-scoped columns this screen would show
          // if the group were open -- not a separately-fetched country
          // total.
          const onCount = group.branches.filter((branch) =>
            organization.branches.includes(branch.name)
          ).length;
          return (
            <StyledCell key={group.key} type={cellType} w={columnWidth}>
              <CountryRollup
                on={onCount}
                total={group.branches.length}
                ariaLabel={String(
                  t('superadmin.settings.countryFolding.rollupAriaLabel', {
                    on: onCount,
                    total: group.branches.length,
                  })
                )}
              />
            </StyledCell>
          );
        }

        return group.branches.map((branch) => {
          const isEnabled = organization.branches.includes(branch.name);

          let content: React.ReactNode;
          if (can_edit) {
            content = (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch
                  checked={isEnabled}
                  onChange={(_, value) =>
                    setOrganizationBranch.mutate({
                      branch: branch.name,
                      value,
                    })
                  }
                  disabled={setOrganizationBranch.isLoading}
                  aria-label={`Toggle ${branch.description || branch.name} for ${organization.name}`}
                  sx={{
                    opacity: setOrganizationBranch.isLoading ? 0.6 : 1,
                    transition: 'opacity 0.2s ease-in-out',
                  }}
                />
                {setOrganizationBranch.isLoading && (
                  <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                )}
              </div>
            );
          } else if (isEnabled) {
            content = <CheckCircleSharpIcon />;
          } else {
            content = <IconHolder />;
          }

          return (
            <StyledCell key={branch.name} type={cellType} w={columnWidth}>
              {content}
            </StyledCell>
          );
        });
      })}
      <StyledCell type={cellType} w="20%">
        <StyledProductCellContainer sx={{ color: '#5A9531' }}>
          <span>
            {organization.co2 ? <CheckCircleSharpIcon /> : <IconHolder />}
          </span>
          <span>
            {organization.esg ? <CheckCircleSharpIcon /> : <IconHolder />}
          </span>
          <span>
            {organization.lca ? <CheckCircleSharpIcon /> : <IconHolder />}
          </span>
        </StyledProductCellContainer>
      </StyledCell>
      <StyledCell
        type={cellType}
        w="10%"
        sx={{ textAlign: 'center', p: '5px' }}
      >
        <Checkbox />
      </StyledCell>
    </StyledRow>
  );
}
